import React, { forwardRef, useRef } from "react";

import { Input, InputProps } from "./Input";
import { IInputNumber, useInputNumber } from "../../hooks";
import { useForkRef } from "../../utils";

export interface InputNumberProps
  extends Omit<InputProps, "value" | "onChange" | "step" | "onBlur">,
    IInputNumber {
  shortenThreshold?: number;
}

export const InputNumber = forwardRef<HTMLInputElement, InputNumberProps>(
  (
    {
      decimalLength = 2,
      decimalSeparator = ".",
      disableAbbreviation = false,
      disabled,
      disableMacros = false,
      invalid,
      step = 1,
      thousandSeparator = ",",
      value: defaultValue,
      onCancel,
      onSave,
      onEnterKeyPress: externalEnterKeyPress,
      allowEmptyValue,
      onChange: externalOnChange,
      onClick: externalOnClick,
      limit,
      placeholder,
      trimDecimals = true,
      min,
      max,
      onBlur: externalOnBlur,
      update = false,
      shortenThreshold = 1000,
      ...rest
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const handleRef = useForkRef(ref, inputRef);

    const {
      value,
      onBlur,
      onKeyDown,
      onChange,
      onClick,
      onFocus,
      onDoubleClick,
    } = useInputNumber(
      {
        decimalLength,
        decimalSeparator,
        disableAbbreviation,
        disableMacros,
        step,
        thousandSeparator,
        onEnterKeyPress: externalEnterKeyPress,
        onChange: externalOnChange,
        onClick: externalOnClick,
        value: defaultValue,
        onCancel,
        onSave,
        allowEmptyValue,
        limit,
        trimDecimals,
        min,
        max,
        onBlur: externalOnBlur,
        update,
        shortenThreshold,
      },
      inputRef
    );

    return (
      <Input
        {...rest}
        ref={handleRef}
        placeholder={placeholder}
        value={value}
        type="text"
        disabled={disabled}
        invalid={invalid}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onFocus={onFocus}
        aria-placeholder={placeholder}
        aria-disabled={disabled}
        aria-invalid={invalid}
      />
    );
  }
);

InputNumber.displayName = "InputNumber";
